import type { Dashboard } from '../types/DashboardType';
import type React from 'react';
import { isAfterEndOfMonth } from '../util/TimeUtil';

/**
 * Outcome of a single BAS readiness check for one organisation.
 * - ok: nothing to do
 * - attention: the accountant should look at this before lodging
 * - neutral: no signal either way (e.g. no payroll for this org)
 */
export type CheckState = 'ok' | 'attention' | 'neutral';

const countState = (count: number | null | undefined): CheckState => ((count || 0) === 0 ? 'ok' : 'attention');

// Amounts come back from the API as floats; compare to the cent to avoid false mismatches.
const sameAmount = (a: number | null | undefined, b: number | null | undefined) => Math.round((a || 0) * 100) === Math.round((b || 0) * 100);

export function bankTransactionsState(entry: Dashboard): CheckState {
  return countState(entry.nbUnreconciledBankTransactions);
}

export function accountTransactionsState(entry: Dashboard): CheckState {
  return countState(entry.nbUnreconciledAccountTransactions);
}

export function paygwState(entry: Dashboard): CheckState {
  return sameAmount(entry.paygwBas, entry.paygwAccount) ? 'ok' : 'attention';
}

export function wagesPayableState(entry: Dashboard): CheckState {
  return sameAmount(entry.wagesPayable, 0) ? 'ok' : 'attention';
}

export function superState(entry: Dashboard): CheckState {
  return Math.round((entry.superPayable || 0) * 100) <= Math.round((entry.superAccrued || 0) * 100) ? 'ok' : 'attention';
}

export function payrunState(yearMonth: string | null, entry: Dashboard): CheckState {
  if (!entry.lastPayrunPosted) {
    return 'neutral';
  }
  return isAfterEndOfMonth(yearMonth, entry.lastPayrunPosted) ? 'ok' : 'attention';
}

const CELL_STYLES: Record<CheckState, React.CSSProperties> = {
  ok: { backgroundColor: '#e7f6ec', color: '#1e6b3a' },
  attention: { backgroundColor: '#fff3e0', color: '#8a4b00', fontWeight: 600 },
  neutral: {},
};

export function checkCellStyle(state: CheckState): React.CSSProperties {
  return CELL_STYLES[state];
}
